import { track } from 'utils/tracking';
import type { UnavailableReason } from '$/components/DateRangePicker/unavailableReason';
import { ComponentAction, ComponentType } from '$/types/tracking';

const TRIP_DETAILS_FORM_NAME = 'ti-search-module-summer';
const TRIP_DETAILS_FORM_TYPE = 'search form';
const VERTICAL_CODE = 'TI';

type TripDetailsTrackingParams = {
  action: ComponentAction;
  componentTitle: string;
  componentType: ComponentType | string;
  formFieldValue?: string;
};

function trackTripDetailsEvent({
  action,
  componentTitle,
  componentType,
  formFieldValue,
}: TripDetailsTrackingParams): void {
  track({
    event: 'form_interact',
    lead_form_name: TRIP_DETAILS_FORM_NAME,
    form_type: TRIP_DETAILS_FORM_TYPE,
    vertical_code: VERTICAL_CODE,
    action,
    component_type: componentType,
    component_title: componentTitle,
    form_field_value: formFieldValue ?? '',
  });
}

const BASE_COMPONENT_PREFIX = 'Ti Search Module';
const OVERLAY_COMPONENT_PREFIX = 'Ti Search Module Overlay';
const OVERLAY_UPDATE_OPEN_TITLE = `${BASE_COMPONENT_PREFIX}_Update`;
const OVERLAY_UPDATE_CLOSE_TITLE = `${OVERLAY_COMPONENT_PREFIX}_Update`;

function resolveComponentPrefix(isOverlay?: boolean): string {
  return isOverlay ? OVERLAY_COMPONENT_PREFIX : BASE_COMPONENT_PREFIX;
}

export function trackTripDetailsHover(attributeName: string): void {
  trackTripDetailsEvent({
    action: 'hover',
    componentTitle: `${BASE_COMPONENT_PREFIX}/Tooltip_${attributeName}`,
    componentType: 'tooltip',
  });
}

export function trackTripDetailsCoverType(
  value: string,
  options: { isOverlay?: boolean } = {},
): void {
  trackTripDetailsEvent({
    action: 'select',
    componentTitle: options.isOverlay
      ? `${OVERLAY_COMPONENT_PREFIX}_Cover type`
      : `${BASE_COMPONENT_PREFIX}_Cover Type`,
    componentType: 'radio-button',
    formFieldValue: value,
  });
}

export function trackTripDetailsDestinationsChange(
  value: string,
  options: { isOverlay?: boolean } = {},
): void {
  trackTripDetailsEvent({
    action: 'select',
    componentTitle: `${resolveComponentPrefix(options.isOverlay)}_Destination`,
    componentType: 'dropdown',
    formFieldValue: value,
  });
}

export function trackTripDetailsDestinationRemoval(
  value: string,
  options: { isOverlay?: boolean } = {},
): void {
  trackTripDetailsEvent({
    action: 'click',
    componentTitle: `${resolveComponentPrefix(options.isOverlay)}_Destination Remove`,
    componentType: 'button',
    formFieldValue: value,
  });
}

export function trackTripDetailsDateChange(
  dateType: 'start' | 'end',
  value: string,
  options: { isOverlay?: boolean } = {},
): void {
  trackTripDetailsEvent({
    action: 'select',
    componentTitle: `${resolveComponentPrefix(options.isOverlay)}_${
      dateType === 'start' ? 'Start' : 'End'
    } date`,
    componentType: 'date-picker',
    formFieldValue: value,
  });
}

export function trackTripDetailsTravellersChange({
  travellerType,
  action,
  value,
  isOverlay,
}: {
  travellerType: 'adult' | 'child';
  action: 'plus' | 'minus';
  value: number;
  isOverlay?: boolean;
}): void {
  trackTripDetailsEvent({
    action: 'click',
    componentTitle: `${resolveComponentPrefix(isOverlay)}_Number of ${
      travellerType === 'adult' ? 'Adult' : 'Children'
    }/${action === 'plus' ? 'Plus' : 'Minus'}`,
    componentType: 'button',
    formFieldValue: String(value),
  });
}

export function trackTripDetailsPopupOpen(): void {
  trackTripDetailsEvent({
    action: 'click',
    componentTitle: OVERLAY_UPDATE_OPEN_TITLE,
    componentType: 'overlay',
  });
}

export function trackTripDetailsPopupClose(): void {
  trackTripDetailsEvent({
    action: 'click',
    componentTitle: OVERLAY_UPDATE_CLOSE_TITLE,
    componentType: 'overlay',
  });
}

// CMS-1853 — discrete date-selection events (GA4 / BigQuery), separate from
// the form_interact envelope above by product decision.

export type TiDateEventContext = {
  market: string;
  brand: string;
  tripType: string;
  /**
   * Which presentation of the picker produced the event (CMS-1854 §4). Taken
   * from where it is rendered — the compact TIOptions modal is the mobile
   * sheet, the inline desktop form is the two-month popover — rather than from
   * a media query, so it always matches what the user actually saw.
   */
  viewport: 'mobile' | 'desktop';
};

export function trackTiDateSelectionStart(context: TiDateEventContext): void {
  track({
    event: 'ti_date_selection_start',
    vertical: VERTICAL_CODE,
    ...context,
  });
}

export function trackTiDateSelectionConfirm(
  context: TiDateEventContext & {
    startDate: string;
    endDate: string;
    tripDurationDays: number;
    advanceDays: number;
  },
): void {
  track({
    event: 'ti_date_selection_confirm',
    vertical: VERTICAL_CODE,
    ...context,
  });
}

export function trackTiDateUnavailableClick(
  context: TiDateEventContext & {
    reason: UnavailableReason;
  },
): void {
  track({
    event: 'ti_date_unavailable_click',
    vertical: VERTICAL_CODE,
    ...context,
  });
}
